import Papa from 'papaparse';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, Fragment, useState, useEffect } from 'react';

import {
    Box,
    List,
    Grid,
    ListItem,
    Typography,
    FormControl,
    ListItemText,
    CircularProgress,
} from '@mui/material';

import {
    Button,
    DataTable,
    Animation,
    Autocomplete,
    CircleLoading,
    SnackBarAlert,
    TablePagination,
} from 'shared';
import {
    startTour,
    TOUR_TYPES,
    completeTour,
    goToNextTour,
    updateTourStep,
    selectIsSkipped,
    selectTourStatus,
    selectTutorialPreference,
} from 'store/slices/tour.slice';
import {
    isValidDate,
    statusOptions,
    sanitizePrice,
    extendedHeaders,
} from 'utils/fileValidationHelpers';
import { Upload } from 'components/upload';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { uploadFileTabStyles } from '../../style/style';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';
import { useInventoryService } from 'store/services/inventory.service';
import { purchaseHeaders, requiredPurchaseHeaders } from '../../utils/customHeaders';
import { headerDescriptions, initialSteps, importButtonStep } from './utils/fileTutorial';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;

const UploadFileTab = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const classes = uploadFileTabStyles();
    const tourId = TOUR_TYPES.INVENTORY_UPLOAD;
    const nextTourId = TOUR_TYPES.EMAIL_FORWARDING;
    const companyData = useSelector((state) => state.company.data);
    const { addFileInventory, getInventoryFiles } = useInventoryService();
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const tourStatus = useSelector((state) => selectTourStatus(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const nextTourStatus = useSelector((state) => selectTourStatus(state, nextTourId));

    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);
    const [files, setFiles] = useState([]);
    const [steps, setSteps] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [alertData, setAlertData] = useState({
        open: false,
        message: '',
        severity: '',
    });
    const [loading, setLoading] = useState(true);
    const [tourRun, setTourRun] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [showStatus, setShowStatus] = useState(false);
    const [tourStage, setTourStage] = useState('upload');
    const [statusOption, setStatusOption] = useState('all');
    const [selectedValues, setSelectedValues] = useState({});
    const [importLoading, setImportLoading] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [processingFiles, setProcessingFiles] = useState(null);

    const handleFileDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles && rejectedFiles.length > 0) {
                const hasFileTypeError = rejectedFiles.some((rejection) =>
                    rejection.errors.some((error) => error.code === 'file-invalid-type'),
                );

                if (hasFileTypeError) {
                    handleErrorAlert(
                        setAlertData,
                        'Invalid file type. Please upload a CSV file (.csv format only).',
                    );
                    return;
                }
            }
            if (!acceptedFiles || acceptedFiles.length === 0) {
                return;
            }
            handleClear();
            const newFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            );
            const uploadedFile = newFiles[0];
            setFiles([uploadedFile]);
            if (newFiles) {
                setFileName(uploadedFile.name);
                Papa.parse(newFiles[0], {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        if (results.data.length > 0) {
                            const firstRow = results.data[0];
                            const formatted = Object.keys(firstRow).map((header) => ({
                                id: header,
                                label: header,
                                sort: false,
                                verticalAlign: 'top',
                                changed: false,
                                labelForSelect: header,
                            }));
                            setHeaders(formatted);
                            setRows(results.data);
                            setData(results.data);
                            setTourRun(false);
                            const manualRestart = tourStatus?.manuallyStarted;

                            if (tourStatus?.completed) {
                                setTourRun(false);
                                return;
                            }

                            if (
                                (tutorialPreference === 'skipped' || isSkipped) &&
                                manualRestart !== true
                            ) {
                                setTourRun(false);
                                return;
                            }

                            dispatch(updateTourStep({ tourId, stepIndex: 1 }));
                            setCurrentStepIndex(0);
                            setTourStage('mapping');

                            setTimeout(() => {
                                const fieldSteps = generateMappingSteps();

                                setSteps(fieldSteps);
                                setTourRun(true);
                            }, 500);
                        }
                    },
                    error: (error) => {
                        handleErrorAlert(setAlertData, 'Error parsing CSV, please try again');
                    },
                });
            }
        },
        [files, dispatch, tourId],
    );

    const handleHeaderChange = (event, ch) => {
        const selectedValue = event.target.value;

        setHeaders((prevHeaders) =>
            prevHeaders.map((header) => {
                if (header.id === selectedValues[ch]) {
                    return {
                        ...header,
                        changed: false,
                        label: header.label.replace(` (${ch.replace(' *', '')})`, ''),
                    };
                }
                if (header.id === selectedValue) {
                    return {
                        ...header,
                        changed: true,
                        label: `${header.label} (${ch.replace(' *', '')})`,
                    };
                }
                return header;
            }),
        );

        setSelectedValues((prevSelectedValues) => ({
            ...prevSelectedValues,
            [ch]: selectedValue,
        }));
    };

    const handleClear = () => {
        setPage(1);
        setRows([]);
        setData([]);
        setFiles([]);
        setHeaders([]);
        setTourRun(false);
        setFileName(null);
        setShowStatus(false);
        setSelectedValues({});
        setStatusOption('all');
        setTourStage('upload');
        setCurrentStepIndex(0);
    };

    const formatData = () => {
        const camelCaseKey = (str) => {
            return str
                .replace(/\([^)]*\)/g, '') // Remove anything inside parentheses, including the parentheses
                .replace(/\*/g, '') // Remove asterisks
                .replace(/(?:^|\s)(\w)/g, (_, c) => (c ? c.toUpperCase() : '')) // Capitalize letters after spaces
                .replace(/\s+/g, '') // Remove spaces
                .replace(/^(\w)/, (_, c) => c.toLowerCase()); // Make the first letter lowercase
        };

        const updatedRows = [];

        const formattedRows = rows.map((row) => {
            const formattedRow = {};
            let status = { valid: true, issues: [] };

            Object.keys(selectedValues).forEach((key) => {
                const mappedKey = camelCaseKey(key);
                const mappedValue = selectedValues[key];
                let value = row[mappedValue];

                if (requiredPurchaseHeaders.includes(key) && (!value || value === '')) {
                    status.valid = false;
                    status.issues.push(`${key} is required`);
                }

                if (key.includes('date') && value) {
                    if (!isValidDate(value)) {
                        status.valid = false;
                        status.issues.push(`${key} is not a valid date`);
                    }
                }

                if (
                    key.toLowerCase().includes('price') ||
                    key.toLowerCase().includes('amount') ||
                    key.toLowerCase().includes('comission')
                ) {
                    if (value) {
                        const number = sanitizePrice(value);
                        if (number === null) {
                            status.valid = false;
                            status.issues.push(`${key} is not a valid number`);
                        }
                        value = number;
                    }
                }

                formattedRow[mappedKey] = row[mappedValue] || null;
            });

            formattedRow._status = status;
            updatedRows.push({ ...row, _status: status });
            return formattedRow;
        });

        setRows(updatedRows);
        setData(updatedRows);
        return formattedRows;
    };

    const handleImport = async () => {
        const data = formatData();

        const invalidRows = data.filter((row) => !row._status.valid);

        if (invalidRows.length > 0) {
            handleErrorAlert(
                setAlertData,
                `${invalidRows.length} row(s) have errors. Please fix them before importing`,
            );
            setShowStatus(true);
            return;
        } else {
            setShowStatus(false);
        }

        setImportLoading(true);

        try {
            const response = await addFileInventory({
                data,
                companyId: companyData.id,
                fileName,
            });
            handleSuccessAlert(setAlertData, response.message);
            handleClear();
            handleGetInventoryFiles();

            if (tutorialPreference === 'skipped' || isSkipped) {
                dispatch(completeTour({ tourId, skipSaving: false }));
                return;
            }
            if (!nextTourStatus?.completed) {
                dispatch(
                    goToNextTour({
                        currentTourId: tourId,
                        nextTourId: nextTourId,
                    }),
                );

                setTimeout(() => {
                    navigate('/profile', { state: { restartTour: true } });
                }, 1000);
            } else {
                dispatch(completeTour({ tourId }));
            }
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setImportLoading(false);
        }
    };

    const handleStepCallback = (data) => {
        const { type, action, index, status } = data;

        if (action === 'skip' || status === 'skipped' || data.type === 'tour:end') {
            setTourRun(false);
            dispatch(completeTour({ tourId }));
            return;
        }
        if (type === 'step:after' && action === 'next') {
            if (tourStage === 'mapping' && index === steps.length - 1) {
                setTourStage('import');
                setTourRun(false);
                setTimeout(() => {
                    setSteps(importButtonStep);
                    setCurrentStepIndex(0);
                    dispatch(updateTourStep({ tourId, stepIndex: 2 }));
                    setTourRun(true);
                }, 300);
            }
        }
    };

    const handleStatusChange = (e, status) => {
        setStatusOption(status.value);
        setPage(1);
        if (status.value === 'all') {
            setData(rows);
        } else if (status.value === 'valid') {
            setData(rows?.filter((row) => row?._status?.valid));
        } else if (status.value === 'invalid') {
            setData(rows?.filter((row) => !row?._status?.valid));
        }
    };

    const handleGetInventoryFiles = async () => {
        try {
            setLoading(true);
            const response = await getInventoryFiles(companyData?.id, 'processing');
            setProcessingFiles(response?.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const generateMappingSteps = () =>
        purchaseHeaders.map((header) => {
            const slug = header.replace(/\s+/g, '-').replace(/[*]/g, '').toLowerCase();

            return {
                target: `[data-tour="map-${slug}"]`,
                content: headerDescriptions[header],
                placement: 'top',
                disableBeacon: false,
                disableOverlay: true,
                offset: -30,
            };
        });

    const handleStepBefore = (idx) => {
        setCurrentStepIndex(idx);

        let globalStepIndex;

        if (tourStage === 'upload') {
            globalStepIndex = 0;
        } else if (tourStage === 'mapping') {
            globalStepIndex = 1 + idx;
        }

        if (globalStepIndex !== undefined) {
            dispatch(updateTourStep({ tourId, stepIndex: globalStepIndex }));
        }
    };

    const handleTourFinish = () => {
        setTourRun(false);

        if (tourStage === 'import') {
            dispatch(completeTour({ tourId }));
        }
    };

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;

        if (
            (tutorialPreference === 'skipped' || isSkipped || tourStatus?.completed) &&
            manualRestart !== true
        ) {
            setTourRun(false);
            return;
        }

        setTimeout(() => {
            if (files.length === 0) {
                dispatch(startTour({ tourId }));
                setCurrentStepIndex(0);
                setSteps(initialSteps);
                setTourRun(true);
            } else if (files.length > 0 && tourStage === 'mapping') {
                setCurrentStepIndex(0);
                setSteps(generateMappingSteps());
                setTourRun(true);
            } else if (files.length > 0 && tourStage === 'import') {
                setCurrentStepIndex(0);
                setSteps(importButtonStep);
                setTourRun(true);
            }
        }, 1000);
    }, [companyData, tourStatus?.completed, tourStatus?.manuallyStarted, files.length, tourStage]);

    useEffect(() => {
        if (!companyData) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `inventory-file/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            handleSuccessAlert(setAlertData, `${data?.file_name} processed successfully`);

            setProcessingFiles((prev) => prev.filter((f) => f.id !== data.id));
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData, processingFiles]);

    useEffect(() => {
        if (companyData) {
            handleGetInventoryFiles();
        }
    }, [companyData]);

    if (!companyData) {
        return <CircleLoading />;
    }

    return (
        <Fragment>
            {steps.length > 0 && (
                <JoyrideTour
                    key={`${tourStage}-${currentStepIndex}`}
                    steps={steps}
                    run={tourRun}
                    continuous={true}
                    spotlightClicks={false}
                    showSkipButton={true}
                    showProgress={true}
                    showBeacon={true}
                    stepIndex={currentStepIndex}
                    onStepBefore={handleStepBefore}
                    onFinish={handleTourFinish}
                    isModal={false}
                    callback={handleStepCallback}
                    disableScrolling={false}
                    styles={classes.joyRideTour}
                />
            )}
            {loading ? (
                <CircleLoading />
            ) : (
                <Fragment>
                    <Grid container sx={classes.inventoryReconciliationWrapper}>
                        {processingFiles && processingFiles?.length > 0 && (
                            <Grid item sx={12} width="100%" marginBottom={2}>
                                <Box margin="auto" padding={2} borderRadius={2} boxShadow={3}>
                                    <Typography variant="h6" marginBottom={2}>
                                        Processing Files
                                    </Typography>
                                    <List>
                                        {processingFiles.map((file) => (
                                            <ListItem
                                                justifyContent="space-between"
                                                display="flex"
                                                key={file.id}
                                            >
                                                <ListItemText primary={file.file_name} />
                                                <CircularProgress size={24} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </Grid>
                        )}
                        <Fragment>
                            <Grid item xs={12} width={'100%'}>
                                <Box data-tour="upload-area">
                                    <Upload
                                        multiple
                                        accept={{
                                            '.csv': [],
                                        }}
                                        placeholder={'Drag and drop your files here (CSV)'}
                                        files={files}
                                        onDrop={handleFileDrop}
                                        onRemove={handleClear}
                                        data-tour="upload-area"
                                    />
                                </Box>
                                {headers.length > 0 && (
                                    <Box sx={classes.buttonsWrapper}>
                                        <Box data-tour="import-button">
                                            <Button
                                                text="Import"
                                                variant="outlined"
                                                disabled={requiredPurchaseHeaders.some(
                                                    (req) => !selectedValues[req],
                                                )}
                                                customStyles={classes.button}
                                                onClick={handleImport}
                                                loading={importLoading}
                                            />
                                        </Box>

                                        <Button
                                            text="Clear"
                                            onClick={handleClear}
                                            customStyles={classes.clearButton}
                                        />
                                    </Box>
                                )}
                            </Grid>
                            {files.length > 0 && (
                                <Grid container marginTop={5} spacing={2}>
                                    <Grid item sm={12} md={6}>
                                        <Typography marginBottom="20px">
                                            Total Rows: {rows.length}
                                        </Typography>
                                        {showStatus && (
                                            <Typography marginBottom="20px" color="error">
                                                Total Rows with error:{' '}
                                                {rows?.filter((row) => !row?._status?.valid).length}
                                            </Typography>
                                        )}
                                    </Grid>
                                    {headers.length > 0 && (
                                        <Grid item sm={12} md={6}>
                                            {purchaseHeaders.map((ch, i) => (
                                                <Box
                                                    key={i}
                                                    sx={classes.dropdownsWrapper}
                                                    data-tour={`map-${ch
                                                        .replace(/\s+/g, '-')
                                                        .replace(/[*]/g, '')
                                                        .toLowerCase()}`}
                                                >
                                                    <Typography sx={classes.flex}>{ch}</Typography>
                                                    <FormControl sx={classes.flex}>
                                                        <Autocomplete
                                                            options={headers.filter(
                                                                (h) =>
                                                                    !h.changed ||
                                                                    selectedValues[ch] === h.id,
                                                            )}
                                                            labelKey="labelForSelect"
                                                            valueKey="id"
                                                            placeholder="Select Column"
                                                            value={
                                                                headers.find(
                                                                    (h) =>
                                                                        h.id === selectedValues[ch],
                                                                ) || null
                                                            }
                                                            onChange={(e, value) => {
                                                                const event = {
                                                                    target: {
                                                                        value: value
                                                                            ? value.id
                                                                            : '',
                                                                    },
                                                                };
                                                                handleHeaderChange(event, ch);
                                                            }}
                                                            disableClearable={false}
                                                        />
                                                    </FormControl>
                                                </Box>
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Fragment>
                    </Grid>

                    {headers.length > 0 && (
                        <Grid
                            container
                            marginTop="10px"
                            sx={classes.inventoryReconciliationWrapper}
                        >
                            {showStatus && (
                                <Grid item xs={6}>
                                    <Autocomplete
                                        options={statusOptions}
                                        labelKey="label"
                                        valueKey="value"
                                        inlineLabel="Status"
                                        placeholder="Select by Status"
                                        value={statusOptions.find(
                                            (type) => type.value == statusOption,
                                        )}
                                        onChange={handleStatusChange}
                                        disableClearable
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} marginTop="5px">
                                <DataTable
                                    headCells={extendedHeaders(headers, showStatus)}
                                    data={data.slice((page - 1) * 10, page * 10)}
                                ></DataTable>
                            </Grid>
                            <Grid item xs={12} marginTop="5px">
                                <TablePagination
                                    page={page}
                                    count={data.length}
                                    onChange={(e, pageNumber) => setPage(pageNumber)}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {importLoading && <Animation title="Uploading your inventory data" />}

                    <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
                </Fragment>
            )}
        </Fragment>
    );
};

export default UploadFileTab;
